import React, { useContext } from 'react';
import classNames from 'classnames';
import { Icon } from '../Icon/Icon';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
const ButtonGroupContext = React.createContext({});
/**
 * Bronson ButtonGroup component.
 * @constructor
 */
export function ButtonGroup({ auto, buttonGroupName, buttonGroupType, children, className, descriptionText, disabled, errorMessage, labelText, labelHidden, required, requiredText, small, testId, ...otherProps }) {
    const contextValue = {
        buttonGroupType,
        buttonGroupName,
    };
    const divClassNameList = classNames('c-button-group', {
        'c-button-group--auto': auto,
        'c-button-group--small': small,
    }, className).trim();
    const legendClassNameList = classNames('c-form-field__label', {
        'u-visually-hidden': labelHidden,
    }).trim();
    /**
     * If {@link errorMessage} is set, render an {@link ErrorMessage} element.
     */
    const renderIfError = () => {
        if (!errorMessage)
            return null;
        return typeof errorMessage === 'string' ? (React.createElement(ErrorMessage, { id: buttonGroupName }, errorMessage)) : (errorMessage);
    };
    /**
     * If {@link required} is set, render a `required` flag according {@link requiredText}.
     */
    const renderIfRequired = () => {
        return required ? React.createElement("span", { "aria-hidden": "true" }, requiredText) : null;
    };
    /**
     * Generates a space-separated string of identifiers for accessibility purposes.
     */
    const renderAriaDescribedBy = () => {
        const description = descriptionText && buttonGroupName ? `description-${buttonGroupName}` : undefined;
        const error = errorMessage && buttonGroupName ? `error-${buttonGroupName}` : undefined;
        return [description, error].filter(Boolean).join(' ');
    };
    return (React.createElement("fieldset", { className: "c-form-field c-form-field--static", role: buttonGroupType === 'radio' ? 'radiogroup' : undefined, disabled: disabled ? true : undefined, "aria-required": buttonGroupType === 'radio' && required ? true : undefined, "aria-invalid": errorMessage ? 'true' : undefined, "aria-describedby": renderAriaDescribedBy() },
        React.createElement("legend", { className: legendClassNameList },
            labelText,
            renderIfRequired()),
        React.createElement("span", { className: "c-form-field__label", id: `description-${buttonGroupName}` }, descriptionText),
        React.createElement("div", { className: divClassNameList, "data-testid": testId, ...otherProps },
            React.createElement(ButtonGroupContext.Provider, { value: contextValue },
                children,
                " ")),
        renderIfError()));
}
ButtonGroup.displayName = 'ButtonGroup';
/**
 * Bronson ButtonGroupItem component (nested).
 * Accepts all relevant native `<input>` props (such as `value`, `defaultChecked`, `disabled`, `onChange`, `aria-*`).
 * The `type`, `name`, `id` and `className` attributes are managed internally and cannot be overridden.
 * @constructor
 */
function ButtonGroupItem({ handle, index, buttonGroupItemIcon, children, testId, ...otherProps }) {
    const context = useContext(ButtonGroupContext);
    const name = handle ?? context.buttonGroupName;
    const iClassNameList = classNames({
        'c-button-group__icon': true,
    }).trim();
    function renderIfButtonGroupItemIcon() {
        if (buttonGroupItemIcon) {
            return React.createElement(Icon, { className: iClassNameList, name: buttonGroupItemIcon });
        }
        return null;
    }
    return (React.createElement("label", { "data-testid": testId, className: "c-button-group__item", htmlFor: `button-group-item-${name}-${index}` },
        React.createElement("input", { className: "c-button-group__input", type: context.buttonGroupType, name: name, id: `button-group-item-${name}-${index}`, "aria-labelledby": `button-group-item-label-${name}-${index}`, style: {
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
            }, ...otherProps }),
        React.createElement("span", { id: `button-group-item-label-${name}-${index}`, className: "c-button-group__label", "aria-hidden": "true" },
            renderIfButtonGroupItemIcon(),
            children)));
}
ButtonGroupItem.displayName = 'ButtonGroup.Item';
ButtonGroup.Item = ButtonGroupItem;
